/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        slides[0].classList.add('is-visible');

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const currSlide = this.slider.querySelector('.announcement__text.is-visible');
            const currSlideIndex = Array.from(currSlide.parentNode.children)
              .indexOf(currSlide);
            const nextSlideIndex = currSlideIndex + 1 < slides.length ? currSlideIndex + 1 : 0;

            if (document.dir === 'rtl') {
              this.slider.scrollTo(
                -Math.abs(nextSlideIndex * slides[nextSlideIndex].clientWidth),
                0
              );
            } else {
              this.slider.scrollTo(slides[nextSlideIndex].offsetLeft - 32, 0);
            }

            slides[nextSlideIndex].classList.add('is-visible');
            slides[currSlideIndex].classList.remove('is-visible');
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}

if (!customElements.get('hotline-status')) {
  const DAY = 86400000;
  const holidaysCache = {};

  /**
   * Parses a time range like "10:00-22:00" into minutes since midnight.
   * @param {string} value - Time range, anything else (e.g. "closed") means closed.
   * @returns {?number[]} [open, close] or null when closed.
   */
  const parseHours = (value) => {
    const match = /(\d{1,2})(?::(\d{2}))?\s*[-–]\s*(\d{1,2})(?::(\d{2}))?/.exec(value || '');
    if (!match) return null;
    return [match[1] * 60 + Number(match[2] || 0), match[3] * 60 + Number(match[4] || 0)];
  };

  /**
   * Formats minutes since midnight as "HH:MM".
   * @param {number} minutes
   * @returns {string}
   */
  const formatTime = (minutes) => [Math.floor(minutes / 60), minutes % 60]
    .map((n) => String(n).padStart(2, '0'))
    .join(':');

  /**
   * Returns the current date and time in Germany, regardless of the visitor's time zone.
   * @returns {{date: Date, minutes: number}} UTC midnight of the German date and minutes since midnight.
   */
  const germanNow = () => {
    const parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date()).forEach(({ type, value }) => { parts[type] = value; });

    return {
      date: new Date(Date.UTC(parts.year, parts.month - 1, parts.day)),
      minutes: (parts.hour % 24) * 60 + Number(parts.minute)
    };
  };

  /**
   * Returns the German public holidays (nationwide + NRW) of a year as "YYYY-MM-DD" strings.
   * @param {number} year
   * @returns {string[]}
   */
  const germanHolidays = (year) => {
    if (!holidaysCache[year]) {
      // Easter Sunday, anonymous Gregorian algorithm
      const a = year % 19;
      const b = Math.floor(year / 100);
      const c = year % 100;
      const h = (19 * a + b - Math.floor(b / 4) - Math.floor((b - Math.floor((b + 8) / 25) + 1) / 3) + 15) % 30;
      const l = (32 + 2 * (b % 4) + 2 * Math.floor(c / 4) - h - (c % 4)) % 7;
      const m = Math.floor((a + 11 * h + 22 * l) / 451);
      const easter = Date.UTC(year, Math.floor((h + l - 7 * m + 114) / 31) - 1, ((h + l - 7 * m + 114) % 31) + 1);

      holidaysCache[year] = [
        ...['01-01', '05-01', '10-03', '11-01', '12-25', '12-26'].map((day) => `${year}-${day}`),
        // Karfreitag, Ostermontag, Christi Himmelfahrt, Pfingstmontag, Fronleichnam
        ...[-2, 1, 39, 50, 60].map((offset) => new Date(easter + offset * DAY).toISOString().slice(0, 10))
      ];
    }

    return holidaysCache[year];
  };

  class HotlineStatus extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector('.hotline-status__text');
      this.hours = {
        weekday: parseHours(this.dataset.weekdayHours),
        weekend: parseHours(this.dataset.weekendHours),
        holiday: parseHours(this.dataset.holidayHours)
      };
      this.specialDays = this.parseSpecialDays(this.dataset.specialDays);

      this.update();
      this.interval = setInterval(this.update.bind(this), 60000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    /**
     * Parses lines like "24.12.2026 10:00-14:00", "31.12.2026 closed" or "06.01.2026".
     * @param {string} value - Textarea contents.
     * @returns {Object} Hours (or null when closed) keyed by "YYYY-MM-DD".
     */
    parseSpecialDays(value) {
      const specialDays = {};
      (value || '').split('\n').forEach((line) => {
        const match = /(\d{1,2})\.(\d{1,2})\.(\d{4})\s*(.*)/.exec(line);
        if (match) {
          const key = `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
          specialDays[key] = match[4].trim() ? parseHours(match[4]) : this.hours.holiday;
        }
      });

      return specialDays;
    }

    /**
     * Returns the hotline hours for a date.
     * @param {Date} date - UTC midnight of the German date.
     * @returns {?number[]} [open, close] or null when closed.
     */
    getHours(date) {
      const key = date.toISOString().slice(0, 10);
      if (key in this.specialDays) return this.specialDays[key];

      if (this.dataset.autoHolidays === 'true' && germanHolidays(date.getUTCFullYear()).includes(key)) {
        return this.hours.holiday;
      }

      return [0, 6].includes(date.getUTCDay()) ? this.hours.weekend : this.hours.weekday;
    }

    /**
     * Refreshes the status text and open/closed state.
     */
    update() {
      const { date, minutes } = germanNow();
      const today = this.getHours(date);
      const isClosedManually = this.dataset.mode === 'closed';
      const isOpen = !isClosedManually && Boolean(today) && minutes >= today[0] && minutes < today[1];
      let text = '';

      if (isClosedManually) {
        text = this.dataset.unavailableText;
      } else if (isOpen) {
        text = this.dataset.openText.replace('[close]', formatTime(today[1]));
      } else {
        for (let i = 0; i < 14; i += 1) {
          const hours = this.getHours(new Date(date.getTime() + i * DAY));
          if (hours && (i > 0 || minutes < hours[0])) {
            text = this.dataset.closedText.replace('[open]', formatTime(hours[0]));
            break;
          }
        }
      }

      this.classList.toggle('is-open', isOpen);
      this.text.textContent = text;
      this.hidden = !text;
    }
  }

  customElements.define('hotline-status', HotlineStatus);
}
